import { useCallback, useEffect, useRef, useState } from "react"

/**
 * "Add to Claude" 섹션 — API 키 발급 직후 또는 키 없이 claude.ai URL 안내.
 *
 * - 4종 탭: Claude Code · Claude Desktop · Cursor · claude.ai (Connectors)
 * - `apiKey` 있으면 Bearer 토큰 포함 스니펫 복사 가능
 * - `apiKey` 없으면 claude.ai 탭만 복사 가능 (OAuth 자동 흐름 — API 키 불필요)
 * - XSS: serviceSlug / serverName / apiKey 는 React JSX 텍스트 콘텐츠로만 삽입
 * - 접근성: role="tablist" + aria-selected + 좌/우 화살표 키 네비
 */

export type AddToClaudeSectionProps = {
	readonly serviceSlug: string
	readonly serverName: string
	/** 발급 직후 평문 키 (undefined 면 claude.ai 탭만 활성화). */
	readonly apiKey?: string
	/** 키 식별용 label (옵션, 표시용). */
	readonly keyLabel?: string
}

type TabId = "claude-code" | "claude-desktop" | "cursor" | "claude-ai"

const TABS: ReadonlyArray<{ readonly id: TabId; readonly label: string }> = [
	{ id: "claude-code", label: "Claude Code" },
	{ id: "claude-desktop", label: "Claude Desktop" },
	{ id: "cursor", label: "Cursor" },
	{ id: "claude-ai", label: "claude.ai" }
]

/**
 * 탭별 스니펫 생성.
 * apiKey 가 undefined 이면 claude-ai 탭 이외의 스니펫에서 placeholder 표기.
 */
function buildSnippet(tab: TabId, origin: string, slug: string, server: string, apiKey?: string): string {
	const mcpUrl = `${origin}/mcp/${slug}/${server}`
	const key = apiKey ?? "<API_KEY>"

	switch (tab) {
		case "claude-code":
			return `claude mcp add --transport http pkn-${slug}-${server} ${mcpUrl} --header "Authorization: Bearer ${key}"`

		case "claude-desktop":
			return JSON.stringify(
				{
					mcpServers: {
						[`pkn-${slug}-${server}`]: {
							url: mcpUrl,
							headers: {
								Authorization: `Bearer ${key}`
							}
						}
					}
				},
				null,
				2
			)

		case "cursor":
			return JSON.stringify(
				{
					mcpServers: {
						[`pkn-${slug}-${server}`]: {
							url: mcpUrl,
							transport: "http",
							headers: {
								Authorization: `Bearer ${key}`
							}
						}
					}
				},
				null,
				2
			)

		case "claude-ai":
			return mcpUrl
	}
}

/** 복사 버튼 — 복사 후 2초간 "복사됨" 라벨로 전환. */
function CopyButton(props: { readonly text: string; readonly disabled?: boolean }) {
	const [copied, setCopied] = useState(false)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleCopy = useCallback(async () => {
		if (props.disabled) return
		try {
			await navigator.clipboard.writeText(props.text)
			setCopied(true)
			if (timerRef.current) clearTimeout(timerRef.current)
			timerRef.current = setTimeout(() => setCopied(false), 2000)
		} catch {
			// HTTPS 또는 localhost 가 아닌 환경에서는 clipboard API 가 차단됨.
			// 별도 fallback 없이 "브라우저에서 수동 복사" 라벨로 안내.
		}
	}, [props.text, props.disabled])

	// 컴포넌트 언마운트 시 타이머 정리
	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [])

	return (
		<button
			type="button"
			className="code-copy"
			onClick={handleCopy}
			disabled={props.disabled}
			aria-label={copied ? "복사됨" : "클립보드에 복사"}
		>
			{copied ? "복사됨" : "복사"}
		</button>
	)
}

/**
 * 코드 블록 + 복사 버튼 조합.
 */
function SnippetBlock(props: { readonly content: string; readonly copyDisabled?: boolean }) {
	return (
		<div className="code-block">
			<pre
				className="code"
				style={{
					whiteSpace: "pre-wrap",
					wordBreak: "break-all",
					overflowX: "auto",
					paddingRight: 64 // 복사 버튼 오버랩 방지
				}}
			>
				{props.content}
			</pre>
			<CopyButton text={props.content} disabled={props.copyDisabled} />
		</div>
	)
}

export function AddToClaudeSection(props: AddToClaudeSectionProps) {
	const [activeTab, setActiveTab] = useState<TabId>("claude-code")
	const tabListRef = useRef<HTMLDivElement>(null)

	const origin = typeof window === "undefined" ? "" : window.location.origin
	const { serviceSlug, serverName, apiKey, keyLabel } = props
	const hasKey = apiKey !== undefined

	/** 탭 클릭 핸들러 */
	const selectTab = useCallback((id: TabId) => {
		setActiveTab(id)
	}, [])

	/** 좌/우 화살표 키 → 인접 탭으로 이동 */
	function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, currentIndex: number) {
		if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
			e.preventDefault()
			const dir = e.key === "ArrowRight" ? 1 : -1
			const nextIndex = (currentIndex + dir + TABS.length) % TABS.length
			const nextTab = TABS[nextIndex]
			if (nextTab) {
				setActiveTab(nextTab.id)
				// 포커스 이동
				const buttons = tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
				buttons?.[nextIndex]?.focus()
			}
		}
	}

	const snippet = buildSnippet(activeTab, origin, serviceSlug, serverName, apiKey)

	// claude.ai 탭은 API 키 없어도 복사 가능 (OAuth 자동 흐름)
	const copyDisabled = !hasKey && activeTab !== "claude-ai"

	return (
		<div
			style={{
				border: "1px solid var(--pk-border)",
				borderRadius: "var(--pk-radius-md)",
				overflow: "hidden",
				background: "var(--pk-bg-surface)"
			}}
		>
			{/* 헤더 */}
			<div
				style={{
					padding: "var(--pk-space-4) var(--pk-space-5) var(--pk-space-3)",
					borderBottom: "1px solid var(--pk-border-subtle)"
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: "var(--pk-space-2)", marginBottom: 4 }}>
					<span style={{ fontSize: 15, fontWeight: 700, color: "var(--pk-text-primary)" }}>
						Add to Claude
					</span>
					{keyLabel && (
						<span
							style={{
								fontSize: 11,
								padding: "1px 8px",
								borderRadius: "var(--pk-radius-pill)",
								background: "var(--pk-accent-subtle)",
								color: "var(--pk-accent)",
								fontFamily: "var(--pk-font-mono)"
							}}
						>
							{keyLabel}
						</span>
					)}
				</div>
				<p
					style={{
						margin: 0,
						fontSize: 12,
						color: "var(--pk-text-muted)",
						lineHeight: 1.5
					}}
				>
					아래 스니펫을 복사해 AI 클라이언트에 붙여넣으면 이 MCP 서버에 연결됩니다.
				</p>
			</div>

			{/* API 키 없을 때 안내 */}
			{!hasKey && (
				<div
					className="alert alert-warn"
					style={{
						margin: "var(--pk-space-4) var(--pk-space-5) 0",
						borderRadius: "var(--pk-radius-sm)"
					}}
				>
					<strong>키를 먼저 발급한 후 복사하세요.</strong> Claude Code · Claude Desktop · Cursor 스니펫은 발급
					직후에만 평문 키를 포함합니다. claude.ai 탭은 API 키 없이 OAuth 2.1 으로 자동 연결됩니다.
				</div>
			)}

			{/* 탭 바 */}
			<div
				role="tablist"
				ref={tabListRef}
				aria-label="Claude 클라이언트 선택"
				style={{
					display: "flex",
					gap: 2,
					padding: "var(--pk-space-3) var(--pk-space-5) 0",
					borderBottom: "1px solid var(--pk-border-subtle)"
				}}
			>
				{TABS.map((tab, idx) => {
					const isActive = activeTab === tab.id
					return (
						<button
							key={tab.id}
							id={`claude-tab-${tab.id}`}
							role="tab"
							type="button"
							aria-selected={isActive}
							aria-controls={`claude-panel-${tab.id}`}
							tabIndex={isActive ? 0 : -1}
							onClick={() => selectTab(tab.id)}
							onKeyDown={(e) => handleKeyDown(e, idx)}
							style={{
								padding: "var(--pk-space-2) var(--pk-space-4)",
								background: "transparent",
								border: "none",
								borderBottom: `2px solid ${isActive ? "var(--pk-accent)" : "transparent"}`,
								color: isActive ? "var(--pk-text-primary)" : "var(--pk-text-tertiary)",
								fontSize: 13,
								fontWeight: isActive ? 600 : 400,
								fontFamily: "inherit",
								cursor: "pointer",
								marginBottom: -1,
								transition: "color var(--pk-ease-fast), border-color var(--pk-ease-fast)"
							}}
						>
							{tab.label}
							{tab.id === "claude-ai" && (
								<span
									style={{
										marginLeft: 6,
										fontSize: 9,
										padding: "1px 5px",
										borderRadius: "var(--pk-radius-pill)",
										background: "var(--pk-success-soft)",
										color: "var(--pk-success)",
										textTransform: "uppercase",
										letterSpacing: "0.06em",
										fontWeight: 600
									}}
								>
									OAuth
								</span>
							)}
						</button>
					)
				})}
			</div>

			{/* 패널 */}
			<div
				id={`claude-panel-${activeTab}`}
				role="tabpanel"
				aria-labelledby={`claude-tab-${activeTab}`}
				style={{ padding: "var(--pk-space-4) var(--pk-space-5)" }}
			>
				{activeTab === "claude-code" && (
					<>
						<p
							style={{
								margin: "0 0 var(--pk-space-3)",
								fontSize: 12,
								color: "var(--pk-text-muted)",
								lineHeight: 1.5
							}}
						>
							터미널에서 아래 명령어를 실행하면 Claude Code 에 MCP 서버가 등록됩니다.
						</p>
						<SnippetBlock content={snippet} copyDisabled={copyDisabled} />
					</>
				)}

				{activeTab === "claude-desktop" && (
					<>
						<p
							style={{
								margin: "0 0 var(--pk-space-3)",
								fontSize: 12,
								color: "var(--pk-text-muted)",
								lineHeight: 1.5
							}}
						>
							<code className="inline">claude_desktop_config.json</code> 의{" "}
							<code className="inline">mcpServers</code> 섹션에 아래 내용을 병합하세요.
						</p>
						<SnippetBlock content={snippet} copyDisabled={copyDisabled} />
					</>
				)}

				{activeTab === "cursor" && (
					<>
						<p
							style={{
								margin: "0 0 var(--pk-space-3)",
								fontSize: 12,
								color: "var(--pk-text-muted)",
								lineHeight: 1.5
							}}
						>
							Cursor의 Settings → MCP Servers 에 추가하거나 <code className="inline">.cursor/mcp.json</code>{" "}
							에 병합하세요.
						</p>
						<SnippetBlock content={snippet} copyDisabled={copyDisabled} />
					</>
				)}

				{activeTab === "claude-ai" && (
					<>
						<p
							style={{
								margin: "0 0 var(--pk-space-3)",
								fontSize: 12,
								color: "var(--pk-text-muted)",
								lineHeight: 1.5
							}}
						>
							claude.ai 의 Settings → Connectors → "Add custom connector" 에 아래 URL 을 붙여넣으면
							브라우저에서 로그인 + 허용 단계를 거쳐 자동 연결됩니다. <strong>API 키 입력 불필요</strong> —
							OAuth 2.1 (DCR + PKCE) 로 Claude 가 알아서 client 를 등록합니다.
						</p>
						<SnippetBlock content={snippet} copyDisabled={false} />
					</>
				)}
			</div>
		</div>
	)
}
